import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, X, Sparkles, BookOpen, Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { HELP_CONTENT, helpContextFromPath } from "@/lib/helpContent";
import { HelpAudience, HelpRoleNotes } from "@/components/HelpRoleExtras";

/**
 * Globale Hilfe-Schwebebutton: kontext-sensitiv – passt sich der aktuellen
 * Route an. Auf bestimmten Vollbild-Routen (Sketch, Annotate, Login etc.)
 * wird er ausgeblendet.
 *
 * Bei Klick öffnet sich ein Bottom-Sheet (mobile-first) mit:
 *   - Titel & Erklärung des aktuellen Bereichs
 *   - Praxis-Tipps
 *   - "KI fragen"-CTA, der den globalen KI-Assistenten anstößt
 *   - Optional: "Handbuch öffnen"-Link, sofern Rolle Direktor/Admin
 */
export function HelpFab() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Active project sub-tab (e.g. "tech", "consumption"). ProjectDetail.jsx
  // broadcasts this via "novatus:project-tab-change" – nullable.
  const [projectTab, setProjectTab] = useState(null);

  // Listen for explicit "open-help" requests (e.g. from in-app onboarding).
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("novatus:help-open", handler);
    return () => window.removeEventListener("novatus:help-open", handler);
  }, []);

  // Listen for project-tab changes from ProjectDetail.
  useEffect(() => {
    const handler = (e) => setProjectTab(e?.detail?.tab || null);
    window.addEventListener("novatus:project-tab-change", handler);
    return () => window.removeEventListener("novatus:project-tab-change", handler);
  }, []);

  // Auto-close on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  if (!user) return null;

  // Hide on routes that need full screen real estate.
  const p = location.pathname;
  if (
    p === "/login" ||
    p === "/register" ||
    p.startsWith("/track/") ||
    p.startsWith("/installer/") ||
    p.endsWith("/sketch") ||
    p.endsWith("/annotate")
  ) return null;

  // Resolve context: when inside a project detail and a tab is broadcast,
  // map "<tab>" → "project.<tab>" – else fallback to URL-based detection.
  let contextId;
  const inProject = /^\/projects\/[^/]+/.test(p);
  if (inProject && projectTab) {
    // Tabs from ProjectDetail: customer, consumption, offer, tech, tasks,
    // messages, media, status, history.
    contextId = `project.${projectTab}`;
  } else {
    contextId = helpContextFromPath(p);
  }
  if (!HELP_CONTENT[contextId]) contextId = "app.global";
  const content = HELP_CONTENT[contextId];

  const askAi = () => {
    window.dispatchEvent(
      new CustomEvent("novatus:ai-open", {
        detail: content.aiSeed ? { seed: content.aiSeed } : {},
      })
    );
    setOpen(false);
  };

  const canSeeHandbook = user.role === "admin" || user.role === "sales_director";

  return (
    <>
      {/* Floating button removed — Help is now triggered via the ?-button in
          AppHeader (event "novatus:help-open"). The panel below stays. */}

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Bottom-sheet panel */}
      <div
        data-testid="help-panel"
        className={`fixed left-0 right-0 bottom-0 z-50 bg-[#0E0E12] border-t border-[#00E575]/40 rounded-t-3xl shadow-2xl transition-transform duration-300 ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "85vh" }}
      >
        <div className="mx-auto max-w-3xl flex flex-col" style={{ maxHeight: "85vh" }}>
          {/* drag handle + header */}
          <div className="flex items-center justify-between p-3 border-b border-zinc-800">
            <div className="flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-[#00E575]/15 border border-[#00E575]/30 flex items-center justify-center">
                <HelpCircle size={17} className="text-[#00E575]" />
              </div>
              <div>
                <div className="text-[12px] uppercase tracking-wider text-[#00E575]/80 font-bold">
                  Hilfe
                </div>
                <h3 className="font-display text-[15.5px] font-bold text-zinc-100 leading-tight">
                  {content.title}
                </h3>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-9 w-9 rounded-xl bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center"
              aria-label="Schließen"
              data-testid="help-panel-close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto p-4 flex-1">
            <HelpAudience content={content} />
            <p className="text-[13.5px] text-zinc-200 leading-relaxed mb-4" data-testid="help-summary">
              {content.summary}
            </p>
            <HelpRoleNotes content={content} />

            {content.workflow && content.workflow.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-2 mt-3">
                  So gehst du vor
                </div>
                <ol className="space-y-1.5 mb-5 list-decimal pl-5">
                  {content.workflow.map((step, i) => (
                    <li key={i} className="text-[12.5px] text-zinc-100 leading-snug">
                      {step}
                    </li>
                  ))}
                </ol>
              </>
            )}

            {content.fields && content.fields.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-2 mt-3">
                  Wichtige Felder erklärt
                </div>
                <ul className="space-y-1.5 mb-5">
                  {content.fields.map((f, i) => (
                    <li
                      key={i}
                      className="text-[12.5px] text-zinc-100 leading-snug flex gap-2 bg-zinc-900/60 border border-zinc-800 rounded-lg px-3 py-2"
                    >
                      <span className="text-sky-300 mt-0.5 flex-shrink-0 font-bold">·</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {content.tips && content.tips.length > 0 && (
              <>
                <div className="text-[10.5px] uppercase tracking-wider text-zinc-400 font-bold mb-2">
                  Praxis-Tipps
                </div>
                <ul className="space-y-2 mb-5">
                  {content.tips.map((tip, i) => (
                    <li
                      key={i}
                      className="text-[13px] text-zinc-100 leading-snug flex gap-2 bg-[#161618] border border-zinc-800 rounded-xl px-3 py-2"
                    >
                      <span className="text-[#00E575] mt-0.5 flex-shrink-0 font-bold">▸</span>
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button
                type="button"
                onClick={askAi}
                data-testid="help-ask-ai-btn"
                className="flex-1 h-11 rounded-xl bg-[#00E575] text-[#09090B] text-[13.5px] font-bold inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] active:scale-[0.97] transition"
              >
                <Sparkles size={15} />
                {content.aiSeed ? "KI fragen (vorgeschlagen)" : "KI-Assistent öffnen"}
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); window.dispatchEvent(new CustomEvent("novatus:whats-new-open")); }}
                data-testid="help-whats-new-btn"
                className="flex-1 h-11 rounded-xl bg-zinc-800 text-zinc-100 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-zinc-700 transition"
              >
                <Gift size={15} /> Was ist neu?
              </button>
              {canSeeHandbook && (
                <a
                  href="/admin/handbook"
                  onClick={() => setOpen(false)}
                  data-testid="help-handbook-link"
                  className="flex-1 h-11 rounded-xl bg-zinc-800 text-zinc-100 text-[13.5px] font-semibold inline-flex items-center justify-center gap-1.5 hover:bg-zinc-700 transition"
                >
                  <BookOpen size={15} /> Handbuch
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default HelpFab;
